import BlogPostCardSettings from 'client/components/core/Sidebar/components/Settings/components/BlogPostCardSettings'
import TextFontUpdater from 'client/components/core/Sidebar/components/Settings/components/Font/TextFontUpdater'
import PostListingLayoutSetting from 'client/components/core/Sidebar/components/Settings/components/PostListingLayoutSetting'
import {
  useUpdateEntity,
  useUpdateProp,
} from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import HtmlAttrId from 'client/components/core/Sidebar/components/Settings/options/HtmlAttrId'
import {
  AlignStyle,
  FontSize,
  ShadowStyle,
} from 'client/components/core/Sidebar/components/Settings/styles'
import BorderRadius from 'client/components/core/Sidebar/components/Settings/styles/BorderRadius'
import LetterSpacing from 'client/components/core/Sidebar/components/Settings/styles/LetterSpacing'
import TextLineHeight from 'client/components/core/Sidebar/components/Settings/styles/TextLineHeight'
import MobileIconUi from 'client/components/core/Sidebar/components/Settings/styles/ui/MobileIconUi'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import { blogSelectors, useBlog } from 'client/store'
import { BlogPostListingInterface } from 'common/types/entities/BlogPostListingInterface'
import React from 'react'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { CheckboxControl } from 'src/editor/shared/settings/checkbox-control'
import { ColorPickerControl } from 'src/editor/shared/settings/controls/color-picker/color-picker-control'
import { MarginGroup } from 'src/editor/shared/settings/groups/margin'
import { SizeAndPositionGroup } from 'src/editor/shared/settings/groups/size-and-position'
import { RangeElement } from 'src/editor/shared/ui-lib/range-element'
import {
  OptionSingleValueType,
  SelectNew,
} from 'src/editor/shared/ui-lib/select-new/select-new'

function BlogPostListingSettings({
  entity,
}: {
  entity: BlogPostListingInterface
}) {
  const updateEntity = useUpdateEntity<BlogPostListingInterface>()
  const updateProp = useUpdateProp(entity)
  const categories = useBlog(blogSelectors.getBlogCategories)
  const { isMobile } = useDeviceModeContext()

  const categoriesChoice = categories.map(category => ({
    label: category.name,
    value: `${category.id}`,
  }))
  categoriesChoice.unshift({ label: 'All categories', value: '' })

  function updateCategories(state: OptionSingleValueType) {
    if (state) {
      const updatedState = state.value
      updateProp('category')(updatedState as string)
    }
  }

  const getDefaultCategoriesOption = (category: string) => {
    const currentDefaultState = categoriesChoice.find(
      state => state.value === category,
    )
    if (currentDefaultState) {
      return currentDefaultState
      // biome-ignore lint/style/noUselessElse: <explanation>
    } else return categoriesChoice[0]
  }

  return (
    <>
      <RangeElement
        label="entity_settings.blog_post_listing.maximum_posts_per_page"
        onChange={updateProp('maxPosts')}
        value={entity.maxPosts}
        allowNegative={false}
      />
      <RangeElement
        label="entity_settings.blog_post_listing.columns_gap"
        onChange={updateProp(isMobile ? 'mobileRowsGap' : 'rowsGap')}
        value={isMobile ? entity.mobileRowsGap : entity.rowsGap}
        allowNegative={false}
      />
      {!entity.layoutType && (
        <>
          {!isMobile && (
            <CheckboxControl
              label="entity_settings.blog_post_listing.display_horizontally.label"
              update={updateProp('displayHorizontally')}
              value={entity.displayHorizontally}
            />
          )}
          {entity.displayHorizontally && !isMobile && (
            <>
              <RangeElement
                label="entity_settings.blog_post_listing.rows_gap"
                onChange={updateProp('columnsGap')}
                value={entity.columnsGap}
                min={1}
                max={50}
                allowNegative={false}
              />
              <RangeElement
                label="entity_settings.blog_post_listing.maximum_posts_per_row"
                onChange={updateProp('maxPostsPerRow')}
                value={entity.maxPostsPerRow}
                min={1}
                max={5}
                allowNegative={false}
              />
            </>
          )}
        </>
      )}
      <SelectNew
        labelText="entity_settings.blog_post_listing.category"
        selectedOption={getDefaultCategoriesOption(entity.category)}
        onChange={updateCategories}
        options={categoriesChoice}
        isMulti={false}
      />
      <PostListingLayoutSetting<BlogPostListingInterface>
        updateEntity={updateEntity}
        entity={entity}
      />
      <BlogPostCardSettings entity={entity} isMobile={isMobile} />
      <GroupTitle>entity_settings.image.header</GroupTitle>
      <ShadowStyle
        shadow={entity.imageBoxShadow}
        mobileShadow={entity.mobileImageBoxShadow}
        update={updateProp('imageBoxShadow')}
        mobileUpdate={updateProp('mobileImageBoxShadow')}
        isTitleNeeded={false}
      />
      <BorderRadius
        label="settings_styles.border_radius.label"
        labelIcon={isMobile ? <MobileIconUi /> : null}
        border={isMobile ? entity.mobileImageBorder : entity.imageBorder}
        update={updateProp(isMobile ? 'mobileImageBorder' : 'imageBorder')}
      />
      <MarginGroup
        label="settings_styles.margin.label"
        margin={isMobile ? entity.mobileImageMargin : entity.imageMargin}
        update={updateProp(isMobile ? 'mobileImageMargin' : 'imageMargin')}
      />
      <GroupTitle>entity_settings.blog_post_listing.title</GroupTitle>
      <FontSize
        update={updateProp('titleFontSize')}
        mobileUpdate={updateProp('mobileTitleFontSize')}
        fontSize={entity.titleFontSize}
        mobileFontSize={entity.mobileTitleFontSize}
      />
      <TextLineHeight
        fontSize={entity.titleFontSize}
        mobileFontSize={entity.mobileTitleFontSize}
        update={updateProp('titleLineHeight')}
        mobileUpdate={updateProp('mobileTitleLineHeight')}
        lineHeight={entity.titleLineHeight}
        mobileLineHeight={entity.mobileTitleLineHeight}
      />
      <TextFontUpdater<BlogPostListingInterface>
        entity={entity}
        update={updateEntity}
        fontFamilyKey={'titleFontFamily'}
        fontWeightKey={'titleFontWeight'}
        fontStyleKey={'titleFontStyle'}
        mobileFontFamilyKey={'mobileTitleFontFamily'}
        mobileFontWeightKey={'mobileTitleFontWeight'}
        mobileFontStyleKey={'mobileTitleFontStyle'}
        fontFileIdKey={'titleFontFileId'}
        mobileFontFileIdKey={'mobileTitleFontFileId'}
      />
      <LetterSpacing
        change={
          isMobile
            ? updateProp('mobileTitleLetterSpacing')
            : updateProp('titleLetterSpacing')
        }
        letterSpacing={
          isMobile ? entity.mobileTitleLetterSpacing : entity.titleLetterSpacing
        }
      />
      <AlignStyle
        align={isMobile ? entity.mobileTitleAlign : entity.titleAlign}
        update={
          isMobile ? updateProp('mobileTitleAlign') : updateProp('titleAlign')
        }
        isFlex={false}
      />
      <ColorPickerControl
        update={updateProp('titleColor')}
        mobileUpdate={updateProp('mobileTitleColor')}
        color={entity.titleColor}
        mobileColor={entity.mobileTitleColor}
      />
      <MarginGroup
        label="settings_styles.margin.label"
        margin={isMobile ? entity.mobileTitleMargin : entity.titleMargin}
        update={updateProp(isMobile ? 'mobileTitleMargin' : 'titleMargin')}
      />
      <GroupTitle>entity_settings.blog_post_listing.description</GroupTitle>
      <FontSize
        update={updateProp('descriptionFontSize')}
        mobileUpdate={updateProp('mobileDescriptionFontSize')}
        fontSize={entity.descriptionFontSize}
        mobileFontSize={entity.mobileDescriptionFontSize}
      />
      <TextLineHeight
        fontSize={entity.descriptionFontSize}
        mobileFontSize={entity.mobileDescriptionFontSize}
        update={updateProp('descriptionLineHeight')}
        mobileUpdate={updateProp('mobileDescriptionLineHeight')}
        lineHeight={entity.descriptionLineHeight}
        mobileLineHeight={entity.mobileDescriptionLineHeight}
      />
      <TextFontUpdater<BlogPostListingInterface>
        entity={entity}
        update={updateEntity}
        fontFamilyKey={'descriptionFontFamily'}
        fontWeightKey={'descriptionFontWeight'}
        fontStyleKey={'descriptionFontStyle'}
        mobileFontFamilyKey={'mobileDescriptionFontFamily'}
        mobileFontWeightKey={'mobileDescriptionFontWeight'}
        mobileFontStyleKey={'mobileDescriptionFontStyle'}
        fontFileIdKey={'descriptionFontFileId'}
        mobileFontFileIdKey={'mobileDescriptionFontFileId'}
      />
      <AlignStyle
        align={
          isMobile ? entity.mobileDescriptionAlign : entity.descriptionAlign
        }
        update={
          isMobile
            ? updateProp('mobileDescriptionAlign')
            : updateProp('descriptionAlign')
        }
        isFlex={false}
      />
      <ColorPickerControl
        update={updateProp('descriptionColor')}
        mobileUpdate={updateProp('mobileDescriptionColor')}
        color={entity.descriptionColor}
        mobileColor={entity.mobileDescriptionColor}
      />
      <MarginGroup
        label="settings_styles.margin.label"
        margin={
          isMobile ? entity.mobileDescriptionMargin : entity.descriptionMargin
        }
        update={updateProp(
          isMobile ? 'mobileDescriptionMargin' : 'descriptionMargin',
        )}
      />
      <GroupTitle>entity_settings.blog_post_listing.categories</GroupTitle>
      <FontSize
        update={updateProp('categoriesFontSize')}
        mobileUpdate={updateProp('mobileCategoriesFontSize')}
        fontSize={entity.categoriesFontSize}
        mobileFontSize={entity.mobileCategoriesFontSize}
      />
      <TextFontUpdater<BlogPostListingInterface>
        entity={entity}
        update={updateEntity}
        fontFamilyKey={'categoriesFontFamily'}
        fontWeightKey={'categoriesFontWeight'}
        fontStyleKey={'categoriesFontStyle'}
        mobileFontFamilyKey={'mobileCategoriesFontFamily'}
        mobileFontWeightKey={'mobileCategoriesFontWeight'}
        mobileFontStyleKey={'mobileCategoriesFontStyle'}
        fontFileIdKey={'categoriesFontFileId'}
        mobileFontFileIdKey={'mobileCategoriesFontFileId'}
      />
      <AlignStyle
        align={isMobile ? entity.mobileCategoriesAlign : entity.categoriesAlign}
        update={
          isMobile
            ? updateProp('mobileCategoriesAlign')
            : updateProp('categoriesAlign')
        }
        isFlex={false}
      />
      <ColorPickerControl
        update={updateProp('categoriesColor')}
        mobileUpdate={updateProp('mobileCategoriesColor')}
        color={entity.categoriesColor}
        mobileColor={entity.mobileCategoriesColor}
      />
      <MarginGroup
        label="settings_styles.margin.label"
        margin={
          isMobile ? entity.mobileCategoriesMargin : entity.categoriesMargin
        }
        update={updateProp(
          isMobile ? 'mobileCategoriesMargin' : 'categoriesMargin',
        )}
      />
      <GroupTitle>entity_settings.blog_post_listing.date</GroupTitle>
      <CheckboxControl
        label="entity_settings.blog_post_listing.show_date.label"
        update={updateProp('showDate')}
        value={entity.showDate}
      />
      {entity.showDate && (
        <>
          <FontSize
            update={updateProp('dateFontSize')}
            mobileUpdate={updateProp('mobileDateFontSize')}
            fontSize={entity.dateFontSize}
            mobileFontSize={entity.mobileDateFontSize}
          />
          <TextFontUpdater<BlogPostListingInterface>
            entity={entity}
            update={updateEntity}
            fontFamilyKey={'dateFontFamily'}
            fontWeightKey={'dateFontWeight'}
            fontStyleKey={'dateFontStyle'}
            mobileFontFamilyKey={'mobileDateFontFamily'}
            mobileFontWeightKey={'mobileDateFontWeight'}
            mobileFontStyleKey={'mobileDateFontStyle'}
            fontFileIdKey={'dateFontFileId'}
            mobileFontFileIdKey={'mobileDateFontFileId'}
          />
          <AlignStyle
            align={isMobile ? entity.mobileDateAlign : entity.dateAlign}
            update={
              isMobile ? updateProp('mobileDateAlign') : updateProp('dateAlign')
            }
            isFlex={false}
          />
          <ColorPickerControl
            update={updateProp('dateColor')}
            mobileUpdate={updateProp('mobileDateColor')}
            color={entity.dateColor}
            mobileColor={entity.mobileDateColor}
          />
          <MarginGroup
            label="settings_styles.margin.label"
            margin={isMobile ? entity.mobileDateMargin : entity.dateMargin}
            update={updateProp(isMobile ? 'mobileDateMargin' : 'dateMargin')}
          />
        </>
      )}
      <GroupTitle>
        entity_settings.blog_post_listing.pagination_label
      </GroupTitle>
      <ColorPickerControl
        update={updateProp('paginationActiveColor')}
        mobileUpdate={updateProp('mobilePaginationActiveColor')}
        color={entity.paginationActiveColor}
        mobileColor={entity.mobilePaginationActiveColor}
        label="entity_settings.blog_post_listing.pagination.active_color"
      />
      <ColorPickerControl
        update={updateProp('paginationActiveBackgroundColor')}
        mobileUpdate={updateProp('mobilePaginationActiveBackgroundColor')}
        color={entity.paginationActiveBackgroundColor}
        mobileColor={entity.mobilePaginationActiveBackgroundColor}
        label="entity_settings.blog_post_listing.pagination.active_background_color"
      />
      <ColorPickerControl
        update={updateProp('paginationInactiveColor')}
        mobileUpdate={updateProp('mobilePaginationInactiveColor')}
        color={entity.paginationInactiveColor}
        mobileColor={entity.mobilePaginationInactiveColor}
        label="entity_settings.blog_post_listing.pagination.non_active_color"
      />
      <ColorPickerControl
        update={updateProp('paginationInactiveBackgroundColor')}
        mobileUpdate={updateProp('mobilePaginationInactiveBackgroundColor')}
        color={entity.paginationInactiveBackgroundColor}
        mobileColor={entity.mobilePaginationInactiveBackgroundColor}
        label="entity_settings.blog_post_listing.pagination.non_active_background_color"
      />
      <SizeAndPositionGroup
        padding={isMobile ? entity?.mobilePadding : entity.padding}
        updatePadding={updateProp(isMobile ? 'mobilePadding' : 'padding')}
        margin={isMobile ? entity.mobileMargin : entity.margin}
        updateMargin={updateProp(isMobile ? 'mobileMargin' : 'margin')}
      />
      <HtmlAttrId attrId={entity.htmlAttrId} />
    </>
  )
}

export default BlogPostListingSettings
